import { useAuthStore } from '../store/auth'
import { getAccessToken, getBrandId, getShopId } from './embedConfig'

/**
 * The login inputs a storefront provides, resolved into a single place the App gate and
 * AuthProvider both read, so their decisions never diverge.
 *
 * Sources, in order: `tradeInConfig.shopId`/`.brandId`; `tradeInConfig.accessToken`; a magic-link
 * token in the URL — either `?login=<uuid>` OR a `/apps/retrade/<uuid>` path segment (v1 parity, see
 * below); a token obtained client-side on the login page (`auth store loginToken`, from SMS/wholesale);
 * and — DEV only — the `VITE_DEV_*` env fallbacks so the harness can run against live Core.
 */
export interface Bootstrap {
  shopId: string
  accessToken: string
  preview: boolean
}

const devEnv = (value: string | undefined) => (import.meta.env.DEV ? value || '' : '')

// Login tokens are UUIDs. v1 accepted them either as `?login=<uuid>` or as a path segment
// (`/apps/retrade/<uuid>`), matching this exact shape (v1's `consumerAuth` regex). We support both.
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i

/** A login token supplied via the URL: `?login=` query param first, else a UUID path segment. */
function urlLoginToken(): string {
  const fromQuery = new URLSearchParams(window.location.search).get('login')
  if (fromQuery) return fromQuery
  const fromPath = window.location.pathname.match(UUID_RE)
  return fromPath ? fromPath[0] : ''
}

export function readBootstrap(): Bootstrap {
  const params = new URLSearchParams(window.location.search)
  return {
    shopId: getShopId() || getBrandId() || devEnv(import.meta.env.VITE_DEV_SHOP_ID),
    accessToken:
      getAccessToken() ||
      // After captureUrlLoginToken() runs the URL is clean and this reads from the store instead;
      // urlLoginToken() is the pre-capture fallback (and covers a token still present in the URL).
      urlLoginToken() ||
      useAuthStore.getState().loginToken ||
      devEnv(import.meta.env.VITE_DEV_ACCESS_TOKEN),
    preview: params.get('preview') === 'true',
  }
}

/**
 * Capture a URL-supplied login token once at startup: persist it to the store and strip it from the
 * address bar, mirroring v1 (its `?login=` handler did `replaceState`, and the path-token route
 * navigated away from the UUID). Keeps the auth token out of the browser history/URL. A storefront
 * that sets `tradeInConfig.accessToken` directly has nothing in the URL to clean, so we skip.
 */
export function captureUrlLoginToken(): void {
  if (getAccessToken()) return
  const token = urlLoginToken()
  if (!token) return
  // Persist so the gate stays authenticated after we remove the token from the URL below.
  useAuthStore.getState().setLoginToken(token)
  const url = new URL(window.location.href)
  url.searchParams.delete('login')
  // Drop the UUID path segment (e.g. /apps/retrade/<uuid> -> /apps/retrade); collapse stray slashes.
  url.pathname = url.pathname.replace(token, '').replace(/\/{2,}/g, '/').replace(/\/$/, '') || '/'
  window.history.replaceState(null, '', url.pathname + url.search + url.hash)
}

/** Reactive bootstrap: re-reads on login/logout so the gate advances or falls back to login. */
export function useBootstrap(): Bootstrap {
  // Subscribe to loginToken so setting it (SMS/wholesale/magic-link) re-renders the gate.
  useAuthStore((s) => s.loginToken)
  // Also subscribe to status: a `tradeInConfig.accessToken` login never sets loginToken, so logout()
  // (status → 'idle') needs its own signal to re-render the gate back to the login screen.
  useAuthStore((s) => s.status)
  return readBootstrap()
}
